import React, { useEffect } from "react";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import "./CookiePolicy.scss";

function CookiePolicy() {
  return (
    <>
      <Header />
      
      <section className="cookie-page">
        <div className="cookie-content">
            <h1 className="cookie-title">Cookie Policy</h1>
            <p className="cookie-last-updated">LAST UPDATED: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            
            <div className="cookie-intro">
              <p>
                This Cookie Policy explains how Talsolution ("Talsolution," "we," "us," or "our") uses cookies and similar tracking technologies 
                on our website and platform (the "Site"). This policy should be read together with our Privacy Policy, which provides additional 
                information about how we collect, use, and protect your personal information.
              </p>
            </div>

            <div className="cookie-section">
              <h2>1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device (computer, tablet, or mobile) when you visit a website. Cookies are 
                widely used to make websites work more efficiently and to provide information to website owners. Cookies allow a website to 
                recognize your device and store some information about your preferences or past actions.
              </p>
          </div>

            <div className="cookie-section">
              <h2>2. How We Use Cookies</h2>
              <p>We use cookies and similar technologies for the following purposes:</p>
              
              <h3>2.1 Essential Cookies</h3>
              <p>
                These cookies are necessary for the Site to function properly. They enable core functionality such as security, network management, 
                and accessibility. You cannot opt out of essential cookies as they are required for the Site to work.
              </p>

              <h3>2.2 Performance and Analytics Cookies</h3>
              <p>
                These cookies help us understand how visitors interact with our Site by collecting and reporting information anonymously. They help 
                us improve the way our Site works, for example, by ensuring that users can find what they are looking for easily. We may use 
                analytics services such as Google Analytics to collect this information. These cookies typically remain on your device for up to 
                2 years or until you delete them.
              </p>

              <h3>2.3 Functionality Cookies</h3>
              <p>
                These cookies allow the Site to remember choices you make (such as your username, language, or region) and provide enhanced, more 
                personalized features. They may also be used to provide services you have requested. These cookies typically remain on your device 
                for up to 1 year or until you delete them.
              </p>

              <h3>2.4 Targeting/Advertising Cookies</h3>
              <p>
                These cookies may be set through our Site by our advertising partners. They may be used to build a profile of your interests and 
                show you relevant content on other sites. They do not store directly personal information but are based on uniquely identifying your 
                browser and internet device.
              </p>
            </div>

            <div className="cookie-section">
              <h2>3. Types of Cookies We Use</h2>
              <p>The following table describes the types of cookies we use and why:</p>
              
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Type</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Session Cookies</strong></td>
                      <td>Maintain your session while you navigate through the Site, enable core functionality</td>
                      <td>Session (deleted when you close your browser)</td>
                    </tr>
                    <tr>
                      <td><strong>Authentication Cookies</strong></td>
                      <td>Remember that you are logged in and identify your account</td>
                      <td>Persistent (typically 30 days or until you log out)</td>
                    </tr>
                    <tr>
                      <td><strong>Preference Cookies</strong></td>
                      <td>Remember your preferences and settings (language, region, display preferences)</td>
                      <td>Persistent (typically up to 1 year)</td>
                    </tr>
                    <tr>
                      <td><strong>Analytics Cookies</strong></td>
                      <td>Help us understand how visitors use our Site (e.g., Google Analytics)</td>
                      <td>Persistent (typically up to 2 years)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cookie-section">
              <h2>4. Third-Party Cookies</h2>
              <p>
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the Site, deliver 
                advertisements, and so on. These third parties may set cookies on your device when you visit our Site.
              </p>
              <p>Some examples of third-party services we may use include:</p>
              <ul className="cookie-list">
                <li><strong>Google Analytics:</strong> To analyze website traffic and user behavior. Google Analytics uses cookies to collect 
                information about how visitors use our Site. You can opt out of Google Analytics by installing the 
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"> Google Analytics Opt-out Browser Add-on</a>.</li>
                <li><strong>Cloud Storage Services:</strong> AWS (Amazon Web Services) may use cookies for service functionality</li>
                <li><strong>Content Delivery Networks:</strong> Third-party CDN services may set cookies for performance optimization</li>
              </ul>
              <p>
                These third parties have their own privacy policies and cookie practices. We encourage you to review their policies to understand 
                how they use cookies and your information.
              </p>
            </div>

            <div className="cookie-section">
              <h2>5. Managing Cookies</h2>
              
              <h3>5.1 Browser Settings</h3>
              <p>
                Most web browsers allow you to control cookies through their settings preferences. You can set your browser to refuse cookies, 
                delete cookies, or alert you when cookies are being sent. You can also delete cookies that have already been set. However, if you 
                disable cookies, some features of our Site may not function properly, including:
              </p>
              <ul className="cookie-list">
                <li>Login and authentication features</li>
                <li>Remembering your preferences and settings</li>
                <li>Certain interactive features that rely on session data</li>
              </ul>
              <p>
                To manage cookies, you can access your browser's settings menu and look for options related to "Privacy," "Security," or "Cookies."
              </p>
              <p>Here are links to help you manage cookies in popular browsers:</p>
              <ul className="cookie-list">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer">Internet Explorer</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>

              <h3>5.2 Opt-Out Tools</h3>
              <p>
                You can also opt out of certain third-party cookies by visiting the Network Advertising Initiative's opt-out page at 
                <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer"> http://www.networkadvertising.org/choices/</a> 
                or the Digital Advertising Alliance's opt-out page at 
                <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer"> http://www.aboutads.info/choices/</a>.
              </p>
            </div>

            <div className="cookie-section">
              <h2>6. Do Not Track Signals</h2>
              <p>
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit that you do not want to have your online 
                activity tracked. Currently, there is no standard for how DNT signals should be interpreted. As a result, our Site does not 
                currently respond to DNT signals. We will continue to monitor developments around DNT browser technology and the implementation 
                of a standard.
              </p>
            </div>

            <div className="cookie-section">
              <h2>7. Updates to This Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory 
                reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last Updated" date. 
                Your continued use of our Site after such changes constitutes your acceptance of the updated Cookie Policy.
              </p>
            </div>

            <div className="cookie-section">
              <h2>8. Contact Us</h2>
              <p>If you have any questions about this Cookie Policy or our use of cookies, please contact us:</p>
              <div className="cookie-contact">
                <p><strong>Talsolution</strong></p>
                <p>2821 Mexico Gravel Rd #5 Columbia MO 65202</p>
                <p>Email: <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a></p>
                <p>Phone: <a href="tel:+12108302282">+1 (210) 830-2282</a></p>
          </div>
        </div>
      </div>
      </section>

      <Footer />
      <Gotop />
    </>
  );
}

export default CookiePolicy;
